/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HomePageContext } from "@insite/content-library/Pages/HomePage";
import { Button, LoadingOverlay, TextField } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import checkPassword from "@monsma/App/Handlers/CommonHandlers/CheckPassword";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const homePageUrl = getPageLinkByPageType(state, HomePageContext)?.url;
    return {
        userRoles: getSession(state).userRoles,
        homePageUrl,
    };
};

const mapDispatchToProps = {
    checkPassword,
};

const enum fields {
    protectedBy = "protectedBy",
    allwoedRoles = "allwoedRoles",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.protectedBy]: "password" | "role";
        [fields.allwoedRoles]: string;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasShellContext &
    HasHistory &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProtectedContentStyles {
    title?: InjectableCss;
    passwordProtectedMessage: InjectableCss;
    passwordFieldWrapper: InjectableCss;
    submitButtonWrapper: InjectableCss;
}

export const protectedContentStyles: ProtectedContentStyles = {
    title: {
        css: css`
            font-size: 22px;
            font-weight: 700;
            margin-bottom: 16px;
            color: "text.main";
        `,
    },
    passwordProtectedMessage: {
        css: css`
            font-size: 15px;
            margin-bottom: 12px;
            line-height: 22px;
            font-weight: normal;
        `,
    },

    passwordFieldWrapper: {
        css: css`
            width: 80%;
        `,
    },

    submitButtonWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
};
const StyledForm = getStyledWrapper("form");

const styles = protectedContentStyles;

const passwordCookieName = "Monsma_ResourcePassword";

const ProtectedContentWidget: FC<Props> = ({
    id,
    userRoles,
    fields,
    shellContext,
    history,
    homePageUrl,
    checkPassword,
}) => {
    const [passwordError, setPasswordError] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState("");

    React.useEffect(() => {
        const isRoleIncluded = !!userRoles && fields.allwoedRoles.split(",").some(role => userRoles.indexOf(role) > -1);
        const hasPassword = !!getCookie(passwordCookieName);
        const protectedByRole = fields.protectedBy === "role";
        const shouldRedirectToHomePage = !(isRoleIncluded && protectedByRole);
        const isAccessAllowed = !!shellContext.isInShell || !shouldRedirectToHomePage || hasPassword;
        setIsAuthenticated(isAccessAllowed);
        setIsLoading(false);
        if (protectedByRole && homePageUrl && !isAccessAllowed) {
            history.replace(homePageUrl);
        }
    }, []);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        checkPassword({
            password,
            onSuccess: (result: any) => {
                if (result === false) {
                    setPasswordError(translate("Password Incorrect"));
                    return;
                }
                setCookie(passwordCookieName, password);
                setIsAuthenticated(true);
            },
            onError: (error: any) => {},
        });
    };

    const handlePasswordChange = (event: any) => {
        setPasswordError("");
        setPassword(event.target.value);
    };

    const showProtectedWidget = isAuthenticated || shellContext.isInShell;
    return (
        <LoadingOverlay
            loading={isLoading}
            css={css`
                width: 100%;
            `}
        >
            {fields.protectedBy === "password" && !isAuthenticated && (
                <StyledWrapper id="passwordProtectedWidget">
                    <StyledForm onSubmit={handleSubmit} noValidate>
                        <StyledWrapper {...styles.title}>
                            <Typography as="h2">{translate("Password Protected")}</Typography>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Typography as="h2" {...styles.passwordProtectedMessage}>
                                {siteMessage("Monsma_Password_Protected_Msg")}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.passwordFieldWrapper}>
                            <TextField
                                type="password"
                                value={password}
                                data-test-selector="passwordProtectedField"
                                onChange={handlePasswordChange}
                                error={passwordError}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.submitButtonWrapper}>
                            <Button type="submit" sizeVariant="medium">
                                {translate("Submit")}
                            </Button>
                        </StyledWrapper>
                    </StyledForm>
                </StyledWrapper>
            )}
            {showProtectedWidget && <Zone zoneName="ProtectedContent" contentId={id} />}
        </LoadingOverlay>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.protectedBy,
            displayName: "Protected By",
            editorTemplate: "RadioButtonsField",
            defaultValue: "horizontal",
            fieldType: "General",
            options: [
                {
                    displayName: "Password",
                    value: "password",
                },
                {
                    displayName: "Role",
                    value: "role",
                },
            ],
        },
        {
            name: fields.allwoedRoles,
            displayName: "Allowed Roles (comma separated)",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            isVisible: item => item?.fields[fields.protectedBy] === "role",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withHistory(ProtectedContentWidget))),
    definition,
};

export default widgetModule;
